"use client"

import clsx from "clsx"
import { useMemo } from "react"
import { Text } from "@/elements"
import { UserMinusIcon } from "@/icons/outline/UserMinusIcon"
import { SlashCircleIcon } from "@/icons/outline/SlashCircleIcon"
import { SendIcon } from "@/icons/outline/SendIcon"
import { UserXIcon } from "@/icons/outline/UserXIcon"
import { UserCheckIcon } from "@/icons/outline/UserCheckIcon"
import { OpenEnvelopeIcon } from "@/icons/outline/OpenEnvelopeIcon"
import { MessageCheckCircleIcon } from "@/icons/outline/MessageCheckCircleIcon"
import { ClockSnoozeIcon } from "@/icons/outline/ClockSnoozeIcon"

export enum JobStatusTagStatus {
  Submitted = "submitted",
  ChatStarted = "chatStarted",
  Hired = "hired",
  Filled = "filled",
  Withdrawn = "withdrawn",
  Invited = "invited",
  InvitationDeclined = "invitationDeclined",
  InvitationExpired = "invitationExpired",
  Applied = "applied",
  Closed = "closed",
  ApplicationsClosed = "applicationsClosed",
}

interface Props {
  status: JobStatusTagStatus
  showStatusPrefix?: boolean
}

export const JobStatusTag: React.FC<Props> = ({ status, showStatusPrefix = true }) => {
  const { statusText, StatusIcon, wrapperClassName, statusIconColor } = useMemo(() => {
    switch (status) {
      case JobStatusTagStatus.Submitted:
        return {
          statusText: "Applied",
          StatusIcon: SendIcon,
          wrapperClassName: "border-primary-3 border-2 box-border",
          statusIconColor: "stroke-primary-3",
        }
      case JobStatusTagStatus.ChatStarted:
        return {
          statusText: "Chat Started",
          StatusIcon: MessageCheckCircleIcon,
          wrapperClassName: "bg-primary-2 w-[123px]",
          statusIconColor: "stroke-primary-6",
        }
      case JobStatusTagStatus.Invited:
        return {
          statusText: "Invited",
          StatusIcon: OpenEnvelopeIcon,
          wrapperClassName: "bg-primary-1",
          statusIconColor: "stroke-primary-6",
        }
      case JobStatusTagStatus.Hired:
        return {
          statusText: "Hired",
          StatusIcon: UserCheckIcon,
          wrapperClassName: "bg-green-light",
          statusIconColor: "stroke-green-default",
        }
      case JobStatusTagStatus.Filled:
        return {
          statusText: "Filled",
          StatusIcon: UserXIcon,
          wrapperClassName: "bg-red-light",
          statusIconColor: "stroke-red-dark",
        }
      case JobStatusTagStatus.Withdrawn:
        return {
          statusText: "Withdrawn",
          StatusIcon: UserMinusIcon,
          wrapperClassName: "bg-bg-3",
          statusIconColor: "stroke-fg-1",
        }
      case JobStatusTagStatus.InvitationDeclined:
        return {
          statusText: "Invitation Declined",
          StatusIcon: SlashCircleIcon,
          wrapperClassName: "bg-red-light",
          statusIconColor: "stroke-red-dark",
        }
      case JobStatusTagStatus.InvitationExpired:
        return {
          statusText: "Invitation Expired",
          StatusIcon: ClockSnoozeIcon,
          wrapperClassName: "bg-bg-3",
          statusIconColor: "stroke-fg-1",
        }

      case JobStatusTagStatus.Closed:
        return {
          statusText: "This job is closed",
          StatusIcon: UserXIcon,
          wrapperClassName: "bg-red-light",
          statusIconColor: "stroke-red-dark",
        }
      case JobStatusTagStatus.ApplicationsClosed:
        return {
          statusText: "Not currently accepting applications",
          StatusIcon: ClockSnoozeIcon,
          wrapperClassName: "bg-bg-3",
          statusIconColor: "stroke-fg-1",
        }

      default:
      case JobStatusTagStatus.Applied:
        return {
          statusText: "Applied",
          StatusIcon: SendIcon,
          wrapperClassName: "bg-primary-1",
          statusIconColor: "stroke-primary-6",
        }
    }
  }, [status])

  return (
    <div className="flex flex-row items-center gap-2">
      {showStatusPrefix && (
        <Text variant="label" className="text-fg-2">
          Status:
        </Text>
      )}
      <div className={clsx("flex h-[30px] flex-row items-center gap-2 rounded-md p-2", wrapperClassName)}>
        <StatusIcon className={clsx("size-[18px]", statusIconColor)} />
        <Text className="capitalize text-fg-1" variant="label">
          {statusText}
        </Text>
      </div>
    </div>
  )
}
